import {
  Panel,
  PanelType,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  TextField,
} from 'office-ui-fabric-react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fieldByIdSelector } from '../../../../features/apiFeatures/fieldSlice';
import { fieldTypeEnumByIdSelector } from '../../../../features/apiFeatures/fieldTypeSlice';
import {
  getAttr,
  getEntityId,
  getRelId,
} from '../../../../features/helpers/apiHelpers';
import {
  fieldTemplateModelHasIdSelector,
  fieldTemplateModelSelector,
  setFieldTemplateAttribute,
} from '../../../../features/pageFeatures/fieldTemplateAdministrationPageSlice';
import { FieldTemplatePanelTextField } from './FieldTemplatePanelTextField';
import { getPanelFieldTemplateHeader } from './GetFieldTemplateHeader';
import { isValidTextLenght } from '../../../../utilities/validFieldTemplate';
import { resolveValidationRules } from '../../../../utilities/validationRuleHelper';
import { validationRuleByFieldId } from '../../../../features/apiFeatures/validationRuleSlice';
import { FIELDTYPE_CONCLUSION } from '../../../../constants/api';

const FieldTemplatePanel = ({
  isOpen,
  onClose,
  fieldTemplateBeingUpdated,
  onSaveButton,
  setInitialFieldTemplateModelText,
  initialFieldTemplateModelText,
}) => {
  const { t: translator } = useTranslation();
  const dispatch = useDispatch();

  const onTextChange = (value) => {
    dispatch(setFieldTemplateAttribute({ value, attributeName: 'text' }));
  };
  const onTitleChange = (_, value) =>
    dispatch(setFieldTemplateAttribute({ value, attributeName: 'name' }));

  const isEditing = useSelector(fieldTemplateModelHasIdSelector);
  const fieldTemplateModel = useSelector(fieldTemplateModelSelector);
  const field = useSelector(
    fieldByIdSelector(getRelId(fieldTemplateModel, 'field')),
  );
  const fieldTypeEnum = useSelector(
    fieldTypeEnumByIdSelector(getRelId(field, 'fieldType')),
  );
  const validationEntity = useSelector(
    validationRuleByFieldId(getEntityId(field)),
  );

  useEffect(() => {
    if (!initialFieldTemplateModelText) {
      const initialModelText = getAttr(fieldTemplateModel, 'text');
      setInitialFieldTemplateModelText(initialModelText);
    }
  }, [
    fieldTemplateModel,
    initialFieldTemplateModelText,
    setInitialFieldTemplateModelText,
  ]);

  useEffect(() => {
    const initialModelText = getAttr(fieldTemplateModel, 'text');
    setInitialFieldTemplateModelText(initialModelText);
  }, [
    fieldTemplateBeingUpdated,
    fieldTemplateModel,
    setInitialFieldTemplateModelText,
  ]);

  const header = getPanelFieldTemplateHeader(
    translator,
    fieldTypeEnum,
    isEditing,
  );

  const { maxLength } = resolveValidationRules(validationEntity);
  // There is an error for rich text fields
  // It returns 2 when 1 character is written
  // This is a check to fix that issue
  const isFieldTypeConclusion = fieldTypeEnum === FIELDTYPE_CONCLUSION;
  const getFieldTemplateTextLength = getAttr(fieldTemplateModel, 'text')
    ? getAttr(fieldTemplateModel, 'text').length -
      (isFieldTypeConclusion ? 1 : 0)
    : 0;

  return (
    <div>
      <Panel
        isOpen={isOpen}
        type={PanelType.medium}
        onDismiss={fieldTemplateBeingUpdated || onClose}
        isLightDismiss={true}
        closeButtonAriaLabel={translator('ariaLabel.panelClose')}
      >
        <div>
          <div className="panel-content">
            <h3 className="panel-header">{header}</h3>
            <TextField
              label={translator('pages.adminFieldTemplates.panelTitleLabel')}
              onChange={onTitleChange}
              placeholder={translator(
                'pages.adminFieldTemplates.panelTitlePlaceholder',
              )}
              value={getAttr(fieldTemplateModel, 'name')}
            />
            <FieldTemplatePanelTextField
              translator={translator}
              fieldTypeEnum={fieldTypeEnum}
              onTextChange={onTextChange}
              value={initialFieldTemplateModelText}
              maxLength={maxLength}
            />
            {maxLength && (
              <p>
                {getFieldTemplateTextLength}{' '}
                {translator('pages.adminFieldTemplates.of')} {maxLength}
              </p>
            )}
          </div>

          <div className="panel-buttons">
            {fieldTemplateBeingUpdated && <Spinner size={SpinnerSize.large} />}
            <PrimaryButton
              text={translator('global.cancel')}
              onClick={onClose}
              disabled={fieldTemplateBeingUpdated}
            />
            <PrimaryButton
              text={translator('global.confirm')}
              onClick={onSaveButton}
              disabled={
                fieldTemplateBeingUpdated ||
                !getAttr(fieldTemplateModel, 'name') ||
                !getAttr(fieldTemplateModel, 'text') ||
                !isValidTextLenght(getFieldTemplateTextLength, maxLength)
              }
            />
          </div>
        </div>
      </Panel>
    </div>
  );
};

export { FieldTemplatePanel };
